import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Clock, ChevronDown, Sparkles } from "lucide-react";
import { NAV, SITE, TODAY_QUICK, OFFICIAL } from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

// Map nav labels (English) to translation keys
const navKey = (label) => {
  const map = {
    Home: "nav.home",
    About: "nav.about",
    "About Sode Matha": "nav.about.sode",
    "Lineage & Legacy": "nav.about.lineage",
    "About Sri Bhootharaja": "nav.about.bhootharaja",
    Branches: "nav.about.branches",
    Deities: "nav.deities",
    Activities: "nav.activities",
    Publications: "nav.activities.publications",
    Institutions: "nav.activities.institutions",
    Events: "nav.events",
    Gallery: "nav.gallery",
    Sevas: "nav.sevas",
    Contact: "nav.contact",
  };
  return map[label] || null;
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const { lang, setLang, t } = useLang();
  const label = (l) => {
    const k = navKey(l);
    return k ? t(k, l) : l;
  };

  return (
    <>
      {/* Top strip */}
      <div className="hidden md:block sticky top-0 z-50 bg-[#3B2A1A] text-[#F2E7D5] text-xs" data-testid="top-strip">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#D4AF37]" /> {t("top.templeHours")}</span>
            <span className="flex items-center gap-2 text-[#D4AF37]/90"><Sparkles className="w-3.5 h-3.5" /> {t("top.today")}: {TODAY_QUICK.seva}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={`tel:${SITE.phone}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition" data-testid="top-phone"><Phone className="w-3.5 h-3.5" /> {SITE.phone}</a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition" data-testid="top-email"><Mail className="w-3.5 h-3.5" /> {SITE.email}</a>
            <div className="flex items-center gap-2 border-l border-[#D4AF37]/30 pl-4">
              <button
                onClick={() => setLang("en")}
                className={`font-semibold transition ${lang === "en" ? "text-[#D4AF37]" : "text-[#F2E7D5]/70 hover:text-[#D4AF37]"}`}
                data-testid="lang-en"
                aria-pressed={lang === "en"}
              >EN</button>
              <span className="text-[#D4AF37]/30">|</span>
              <button
                onClick={() => setLang("kn")}
                className={`font-semibold transition ${lang === "kn" ? "text-[#D4AF37]" : "text-[#F2E7D5]/70 hover:text-[#D4AF37]"}`}
                data-testid="lang-kn"
                aria-pressed={lang === "kn"}
              >ಕನ್ನಡ</button>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header
        className="sticky top-0 md:top-9 z-40 border-b border-[#D4AF37]/25 backdrop-blur-xl bg-[#FBF7F0]/85"
        data-testid="main-navbar"
      >
        <div className="max-w-none mx-auto pl-3 sm:pl-4 lg:pl-6 pr-4 lg:pr-8 flex items-center justify-between h-24 gap-3 sm:gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 sm:gap-4 group min-w-0" data-testid="nav-logo">
            <div className="relative w-20 h-20 shrink-0">
              <img
                src={OFFICIAL.logo}
                alt="Sri Sode Vadiraja Matha"
                className="w-20 h-20 object-contain transition-transform group-hover:scale-105"
              />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-cinzel text-[15px] sm:text-[22px] lg:text-[18px] tracking-normal sm:tracking-[0.18em] text-[#B65A14] font-semibold truncate">
                {lang === "kn" ? "ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠ" : "SRI SODE VADIRAJA MATHA"}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) => {
              if (item.children) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <button
                      className="px-3 py-2 text-[15px] tracking-wide font-medium text-[#3B2A1A] hover:text-[#B65A14] transition flex items-center gap-1"
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {label(item.label)} <ChevronDown className="w-3 h-3 opacity-60" />
                    </button>
                    {openMenu === item.label && (
                      <div className="absolute top-full left-0 pt-2 min-w-[220px]" data-testid={`submenu-${item.label.toLowerCase()}`}>
                        <div className="bg-[#FBF7F0] border border-[#D4AF37]/40 rounded-xl shadow-2xl overflow-hidden">
                          {item.children.map((c) => (
                            <NavLink
                              key={c.to}
                              to={c.to}
                              className={({ isActive }) =>
                                `block px-4 py-2.5 text-sm border-l-2 ${isActive ? "border-[#D4AF37] bg-[#F2E7D5]/60 text-[#B65A14]" : "border-transparent text-[#3B2A1A] hover:bg-[#F2E7D5]/50 hover:border-[#D4AF37]/60"}`
                              }
                              data-testid={`nav-sub-${c.to.replace("/", "")}`}
                            >
                              {label(c.label)}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3 py-2 text-[15px] tracking-wide font-medium transition ${isActive ? "text-[#B65A14]" : "text-[#3B2A1A] hover:text-[#B65A14]"}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {label(item.label)}
                </NavLink>
              );
            })}
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-2">
            <Link
              to="/rooms"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#3B2A1A] px-5 py-2.5 text-sm font-semibold hover:bg-[#B48F30] transition shadow-md whitespace-nowrap"
              data-testid="nav-cta-rooms"
            >
              {t("nav.bookRoom")}
            </Link>
            <Link
              to="/sevas"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-5 py-2.5 text-sm font-semibold hover:bg-[#8F4610] transition shadow-md whitespace-nowrap"
              data-testid="nav-cta-donate"
            >
              {t("nav.bookSeva")}
            </Link>
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-md text-[#3B2A1A] hover:bg-[#F2E7D5]"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-[#D4AF37]/25 bg-[#FBF7F0]" data-testid="mobile-menu">
            <div className="max-h-[70vh] overflow-y-auto px-4 py-4 space-y-1">
              {NAV.flatMap((item) => (item.children ? item.children : [item])).map((item, idx) => (
                <Link
                  key={`${item.to}-${idx}`}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`block px-3 py-2.5 rounded-lg text-sm ${location.pathname === item.to ? "bg-[#F2E7D5] text-[#B65A14] font-semibold" : "text-[#3B2A1A] hover:bg-[#F2E7D5]/60"}`}
                  data-testid={`mobile-link-${item.to.replace("/", "") || "home"}`}
                >
                  {label(item.label)}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
